import { useEffect, useMemo, useState } from "react";

/** SVG rendered via <img src="data:image/svg+xml..."> runs in the browser's
 * "secure static mode", which silently refuses to fetch any external
 * resource referenced inside it (e.g. a remote photo via <image href>) even
 * with CORS headers present. Markers embed the avatar photo inside such an
 * SVG, so the raw remote URL never renders — only a same-origin/data URI
 * does. This resolves each remote photo to a base64 data URI once so it can
 * be embedded directly. */
const dataUrlCache = new Map();

const toDataUrl = (url) =>
  fetch(url)
    .then((res) => res.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        }),
    );

export const useAvatarDataUrls = (imageUrls) => {
  const [version, setVersion] = useState(0);
  const key = imageUrls.join("|");

  useEffect(() => {
    const uncached = imageUrls.filter((url) => url && !dataUrlCache.has(url));
    if (uncached.length === 0) return undefined;

    let cancelled = false;
    Promise.all(
      uncached.map((url) =>
        toDataUrl(url)
          .then((dataUrl) => dataUrlCache.set(url, dataUrl))
          .catch(() => {}),
      ),
    ).then(() => {
      if (!cancelled) setVersion((n) => n + 1);
    });

    return () => {
      cancelled = true;
    };
  }, [key]);

  /** A snapshot rather than the mutable cache itself, so consumers can key
   * memoized work off its identity: it changes only when the resolved photos
   * do, not on every render. */
  return useMemo(() => new Map(dataUrlCache), [key, version]);
};
